import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { pickLocale } from "../../lib/format";
import AdfImage from "./AdfImage";

/**
 * AI Try-On, in the reference design: a single bordered box on the page ground,
 * copy in the left half and the photograph filling the right.
 *
 * The button goes to the real flow. Try-On is opened per garment
 * (`openTryOn(product)` needs an actual product), so there is nothing to launch
 * from a page with no garment selected; sending the shopper to the filtered
 * catalogue is the honest route, and every card there carries its own entry
 * point.
 */
export default function AITryOnFeature({ block }) {
  const { locale, t } = useI18n();
  if (!block) return null;

  const eyebrow = pickLocale(block.eyebrow, locale);
  const title = pickLocale(block.title, locale);
  const text = pickLocale(block.text, locale);
  const cta = pickLocale(block.cta_label, locale);
  const secondary = pickLocale(block.secondary_label, locale);
  if (!title && !block.image_url) return null;

  return (
    <section className="py-10 md:py-14 bg-white" data-testid="home-tryon">
      <div className="container mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden bg-charcoal/5 w-full flex flex-col md:flex-row items-center border border-charcoal/10">
          <div className="md:w-1/2 w-full p-12 md:p-20 flex flex-col justify-center items-start">
            {eyebrow && (
              <div className="inline-flex items-center space-x-2 text-[10px] tracking-[0.1em] uppercase font-semibold text-charcoal/60 mb-6 border-b border-charcoal/10 pb-2">
                <Sparkles size={12} strokeWidth={1.5} aria-hidden="true" />
                <span>{eyebrow}</span>
              </div>
            )}
            {title && (
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal leading-tight mb-4 tracking-tight">
                {title}
              </h2>
            )}
            {text && (
              <p className="text-charcoal/70 mb-8 max-w-sm text-sm font-medium leading-relaxed">{text}</p>
            )}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              {cta && block.cta_link && (
                <Link
                  to={block.cta_link}
                  className="border-b border-charcoal text-xs font-semibold uppercase tracking-[0.1em] hover:text-charcoal/70 hover:border-charcoal/70 transition-colors flex items-center gap-2 pb-1"
                  data-testid="home-tryon-cta"
                >
                  {cta} <span className="text-lg leading-none">→</span>
                </Link>
              )}
              {secondary && block.secondary_link && (
                <Link
                  to={block.secondary_link}
                  className="text-xs font-medium uppercase tracking-[0.1em] text-charcoal/60 hover:text-charcoal transition-colors"
                >
                  {secondary}
                </Link>
              )}
            </div>
            <p className="mt-8 text-[10px] tracking-[0.1em] uppercase text-charcoal/45 font-medium">
              {t("home.tryon.steps", "Şəklinizi yükləyin · Parçanı seçin · Üzərinizdə görün")}
            </p>
          </div>
          <div className="md:w-1/2 w-full h-[300px] md:h-full md:min-h-[400px] relative self-stretch">
            {block.image_url && (
              <AdfImage
                source={block.image_url}
                mobileSource={block.mobile_image_url}
                alt=""
                sizes="(min-width: 768px) 50vw, 100vw"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
